export type ProductWithPrice = {
  pricebookId: string;
  categoryIds: string[];
  price: number;
  quantity: number;
  taxable?: boolean;
};

export type ModifierWithPrice = {
  discountsAndFeesId: string;
  amount: number;
  taxable?: boolean;
  displayName?: string;
  limit: number;
  roundingRule?: {
    type: 'UP' | 'DOWN' | 'NEAREST';
    decimalPlaces: number;
  };
  amountType: 'PERCENTAGE' | 'FIXED';
  taxBasis?: 'PRE_TAX' | 'POST_TAX';
  qualifyingPricebookIds: string[];
  qualifyingPricebookCategoryIds: string[];
};

type AppliedModifier = {
  discountsAndFeesId: string;
  displayName: string;
  amount: number;
  count: number;
  qualifyingPricebookItems: { pricebookId: string; amount: number }[];
};

type ProductMap = Record<
  string,
  {
    totalPrice: number;
    taxableTotal: number;
    totalWithTax: number;
    qualifyingRebateIds: Set<string>;
  }
>;

type ProductTotals = {
  productsTotal: number;
  productsTotalTaxable: number;
  productsTotalTax: number;
  productsMap: ProductMap;
};

export interface CostsTotalCalculatorResponse {
  discountsTotal: number;
  rebatesTotal: number;
  feesTotal: number;
  feesTotalTax: number;
  feesTotalTaxable: number;
  productsTotal: number;
  productsTotalTax: number;
  productsTotalTaxable: number;
  adjustmentsTotal: number;
  totalPreTax: number;
  totalWithTax: number;
  totalWithRebates: number;
  totalTax: number;
  appliedDiscounts: AppliedModifier[];
  appliedFees: AppliedModifier[];
  appliedRebates: AppliedModifier[];
}

export interface CostsTotalCalculatorRequest {
  products: ProductWithPrice[];
  discounts: ModifierWithPrice[];
  fees: ModifierWithPrice[];
  rebates: ModifierWithPrice[];
  config: { taxRate: number };
}

function withLimit(value: number, limit: number) {
  return Math.min(value, limit || Number.MAX_SAFE_INTEGER);
}

function calculateDiscounts(
  discounts: ModifierWithPrice[],
  productsTotal: number
) {
  let discountsTotal = 0;
  const appliedDiscountsMap = new Map<string, AppliedModifier>();

  for (const discount of discounts) {
    const { amount, amountType, limit } = discount;

    const modifierTotal =
      amountType === 'PERCENTAGE' ? amount * productsTotal : amount;

    const modifierReduction = withLimit(modifierTotal, limit);

    discountsTotal += modifierReduction;

    const existing = appliedDiscountsMap.get(discount.discountsAndFeesId);
    appliedDiscountsMap.set(discount.discountsAndFeesId, {
      discountsAndFeesId: discount.discountsAndFeesId,
      displayName: discount.displayName || 'Discount',
      amount: (existing?.amount ?? 0) + modifierReduction,
      count: (existing?.count ?? 0) + 1,
      qualifyingPricebookItems: [],
    });
  }

  return {
    discountsTotal,
    appliedDiscounts: Array.from(appliedDiscountsMap.values()),
  };
}

function calculateFees(modifiers: ModifierWithPrice[], productsTotal: number) {
  let feesTotal = 0;
  let feesTotalTaxable = 0;
  const appliedFees: AppliedModifier[] = [];

  for (const modifier of modifiers) {
    const { amount, amountType, limit } = modifier;

    const modifierTotal =
      amountType === 'PERCENTAGE' ? amount * productsTotal : amount;

    const modifierFee = withLimit(modifierTotal, limit);
    const taxableAmount = modifier.taxable ? modifierFee : 0;

    feesTotalTaxable += taxableAmount;
    feesTotal += modifierFee;

    appliedFees.push({
      discountsAndFeesId: modifier.discountsAndFeesId,
      displayName: modifier.displayName || 'Fee',
      amount: modifierFee,
      count: 1,
      qualifyingPricebookItems: [],
    });
  }

  return { feesTotal, feesTotalTaxable, appliedFees };
}

function calculateRebates(
  rebates: ModifierWithPrice[],
  totalPreTax: number,
  totalWithTax: number,
  qualifyingItems: { productsMap: ProductMap }
) {
  let rebatesTotal = 0;
  const appliedRebates: AppliedModifier[] = [];
  const qualifyingRebateIds = Object.values(
    qualifyingItems.productsMap
  ).flatMap((p) => Array.from(p.qualifyingRebateIds));

  for (const rebate of rebates) {
    const {
      amount,
      amountType,
      limit,
      roundingRule,
      taxBasis,
      qualifyingPricebookIds,
      qualifyingPricebookCategoryIds,
    } = rebate;

    const hasQualifyingItems =
      qualifyingPricebookIds.length || qualifyingPricebookCategoryIds.length;
    if (
      hasQualifyingItems &&
      !qualifyingRebateIds.includes(rebate.discountsAndFeesId)
    ) {
      continue;
    }

    const round = (value: number) => {
      const factor = Math.pow(10, roundingRule?.decimalPlaces ?? 2);
      return roundingRule?.type === 'UP'
        ? Math.ceil(value * factor) / factor
        : roundingRule?.type === 'DOWN'
          ? Math.floor(value * factor) / factor
          : Math.round(value * factor) / factor;
    };

    const isPreTax = taxBasis === 'PRE_TAX';

    const calculateAmount = (productTotal: number) =>
      amountType === 'PERCENTAGE' ? round(amount * productTotal) : amount;

    const qualifyingPricebookItems = hasQualifyingItems
      ? Object.entries(qualifyingItems.productsMap).flatMap(
          ([pricebookId, p]) =>
            p.qualifyingRebateIds.has(rebate.discountsAndFeesId)
              ? [
                  {
                    pricebookId,
                    amount: calculateAmount(
                      isPreTax ? p.totalPrice : p.totalPrice + p.totalWithTax
                    ),
                  },
                ]
              : []
        )
      : [];

    const modifierTotal = qualifyingPricebookItems.length
      ? round(
          qualifyingPricebookItems
            .map((p) => p.amount)
            .reduce((a, b) => a + b, 0)
        )
      : calculateAmount(isPreTax ? totalPreTax : totalWithTax);

    const modifierRebate = withLimit(modifierTotal, limit);

    rebatesTotal += modifierRebate;

    appliedRebates.push({
      discountsAndFeesId: rebate.discountsAndFeesId,
      qualifyingPricebookItems,
      displayName: rebate.displayName || 'Rebate',
      amount: modifierRebate,
      count: 1,
    });
  }

  return { rebatesTotal, appliedRebates };
}

function calculateDiscountsAndFees(req: CostsTotalCalculatorRequest) {
  let productsPreTotal = 0;

  for (const product of req.products) {
    productsPreTotal += product.price * product.quantity;
  }

  const { discountsTotal, appliedDiscounts } = calculateDiscounts(
    req.discounts,
    productsPreTotal
  );
  const { feesTotal, feesTotalTaxable, appliedFees } = calculateFees(
    req.fees,
    productsPreTotal
  );

  const { productsTotal, productsTotalTaxable, productsTotalTax, productsMap } =
    req.products.reduce<ProductTotals>(
      (prev, item) => {
        const totalPrice = item.price * item.quantity;

        const discountPrice =
          totalPrice -
          (productsPreTotal > 0
            ? discountsTotal * (totalPrice / productsPreTotal)
            : 0);

        const taxableTotal = item.taxable ? discountPrice : 0;
        const totalWithTax = item.taxable
          ? discountPrice * req.config.taxRate
          : 0;

        const prevProductTotal = prev.productsMap[item.pricebookId] || {
          totalPrice: 0,
          taxableTotal: 0,
          totalWithTax: 0,
          qualifyingRebateIds: new Set<string>(),
        };

        const itemIds = [...item.categoryIds, item.pricebookId];

        req.rebates
          .filter((r) =>
            [
              ...r.qualifyingPricebookIds,
              ...r.qualifyingPricebookCategoryIds,
            ].some((itemId) => itemIds.includes(itemId))
          )
          .forEach((r) =>
            prevProductTotal.qualifyingRebateIds.add(r.discountsAndFeesId)
          );

        return {
          ...prev,
          productsMap: {
            ...prev.productsMap,
            [item.pricebookId]: {
              totalPrice: prevProductTotal.totalPrice + totalPrice,
              taxableTotal: prevProductTotal.taxableTotal + taxableTotal,
              totalWithTax: prevProductTotal.totalWithTax + totalWithTax,
              qualifyingRebateIds: prevProductTotal.qualifyingRebateIds,
            },
          },
          productsTotal: prev.productsTotal + totalPrice,
          productsTotalTaxable: prev.productsTotalTaxable + taxableTotal,
          productsTotalTax: prev.productsTotalTax + totalWithTax,
        };
      },
      {
        productsTotalTax: 0,
        productsTotalTaxable: 0,
        productsTotal: 0,
        productsMap: {},
      }
    );

  return {
    productsMap,
    productsTotal,
    productsTotalTaxable,
    productsTotalTax,
    discountsTotal,
    appliedDiscounts,
    feesTotal,
    feesTotalTaxable,
    appliedFees,
  };
}

export function calculateOptionCosts(
  req: CostsTotalCalculatorRequest
): CostsTotalCalculatorResponse {
  const {
    productsMap,
    feesTotal,
    feesTotalTaxable,
    discountsTotal,
    productsTotal,
    productsTotalTaxable,
    productsTotalTax,
    appliedDiscounts,
    appliedFees,
  } = calculateDiscountsAndFees(req);

  const adjustmentsTotal = feesTotal - discountsTotal;
  const feesTotalTax = feesTotalTaxable * req.config.taxRate;
  const totalPreTax = productsTotal + adjustmentsTotal;
  const totalTax = productsTotalTax + feesTotalTax;
  const totalWithTax = totalPreTax + totalTax;

  const { rebatesTotal, appliedRebates } = calculateRebates(
    req.rebates,
    totalPreTax,
    totalWithTax,
    { productsMap }
  );

  return {
    discountsTotal,
    rebatesTotal,
    feesTotal,
    feesTotalTax,
    feesTotalTaxable,
    productsTotal,
    productsTotalTax,
    productsTotalTaxable,
    adjustmentsTotal,
    totalPreTax,
    totalWithTax,
    totalTax,
    totalWithRebates: totalWithTax - rebatesTotal,
    appliedDiscounts,
    appliedRebates,
    appliedFees,
  };
}

export interface MapLinkedItemsRequest {
  pricebookId: string;
  categoryIds: string[];
  price: number;
  membershipPrice?: number;
  hasMembership?: boolean;
  quantity: number;
  taxable?: boolean;
  linkedItems?: {
    pricebookId: string;
    value: number;
    quantity: number;
    taxable: boolean;
    chargeable: boolean;
    categoryIds?: string[];
    type: 'SERVICE' | 'EQUIPMENT' | 'MATERIAL';
  }[];
  type: 'SERVICE' | 'EQUIPMENT' | 'MATERIAL';
}

export interface MapLinkedItemsResponse {
  pricebookId: string;
  categoryIds: string[];
  price: number;
  quantity: number;
  taxable: boolean;
}

export function mapLinkedItems(
  products: MapLinkedItemsRequest[],
  options: { linkedItemsChargeable: boolean; useMembershipPrice: boolean }
): MapLinkedItemsResponse[] {
  return products.flatMap((p) =>
    (p.linkedItems && p.type === 'EQUIPMENT' && options.linkedItemsChargeable
      ? p.linkedItems
          .filter((li) => li.chargeable)
          .map((li) => ({
            price: li.value,
            quantity: li.quantity,
            taxable: li.taxable,
            pricebookId: li.pricebookId,
            categoryIds: li.categoryIds || [],
          }))
      : []
    ).concat({
      price:
        options.useMembershipPrice && p.hasMembership && p.membershipPrice
          ? p.membershipPrice
          : p.price,
      quantity: p.quantity,
      taxable: !!p.taxable,
      pricebookId: p.pricebookId,
      categoryIds: p.categoryIds,
    })
  );
}

export const resolveOptionDownPayment = <
  T extends { type: 'PERCENTAGE' | 'FIXED'; amount: number },
>(
  downPayment: T | undefined,
  totalWithTax: number
): number => {
  if (!downPayment) {
    return 0;
  }

  if (downPayment.type === 'PERCENTAGE') {
    return (totalWithTax * downPayment.amount) / 100;
  }

  return downPayment.amount;
};

export interface FinancingRateCosting {
  id: string;
  name?: string;
  interestRate: number;
  termMonths: number;
  promoEnabled: boolean;
  promoInterestRate?: number;
  promoTermMonths?: number;
  promoNoPaymentEnabled?: boolean;
  applicationUrl?: string;
}

export interface FinancingOptionWithPayment {
  id: string;
  name?: string;
  interestRate: number;
  termMonths: number;
  monthlyPayment: number;
  promoMonthlyPayment: number;
  promoEnabled: boolean;
  promoInterestRate: number;
  promoTermMonths: number;
  promoNoPaymentEnabled: boolean;
  applicationUrl?: string;
}

function baseCalculateMonthlyPayment(
  total: number,
  interestRate: number,
  termMonths: number
) {
  if (termMonths === 0) {
    return total;
  }

  if (interestRate === 0) {
    return total / termMonths;
  }

  const monthlyRate = interestRate / 12;
  const numerator = total * monthlyRate * Math.pow(1 + monthlyRate, termMonths);
  const denominator = Math.pow(1 + monthlyRate, termMonths) - 1;

  return numerator && denominator ? numerator / denominator : 0;
}

function calculatePrincipalPaid({
  balance,
  monthlyRate,
  monthlyPayment,
  monthsLeft,
}: {
  balance: number;
  monthlyRate: number;
  monthlyPayment: number;
  monthsLeft: number;
}): number {
  if (monthsLeft === 0 || balance <= 0) {
    return 0;
  }

  let totalPrincipalPaid = 0;
  let currentBalance = balance;

  for (let month = 0; month < monthsLeft && currentBalance > 0; month++) {
    const interest = currentBalance * monthlyRate;
    const principal = monthlyPayment - interest;
    const actualPrincipal = Math.min(principal, currentBalance);
    totalPrincipalPaid += actualPrincipal;
    currentBalance -= actualPrincipal;
  }

  return totalPrincipalPaid;
}

export function calculateMonthlyPayment(
  total: number,
  costings: Omit<FinancingRateCosting, 'id'>
) {
  const isMonthlyPaymentAffected =
    costings.promoEnabled && !costings.promoNoPaymentEnabled;

  const promoMonthlyPayment = isMonthlyPaymentAffected
    ? baseCalculateMonthlyPayment(
        total,
        costings.promoInterestRate ?? 0,
        costings.termMonths
      )
    : 0;

  const principalPaid = isMonthlyPaymentAffected
    ? calculatePrincipalPaid({
        balance: total,
        monthlyRate: (costings.promoInterestRate ?? 0) / 12,
        monthlyPayment: promoMonthlyPayment,
        monthsLeft: costings.promoTermMonths ?? 0,
      })
    : 0;

  const monthlyPayment = baseCalculateMonthlyPayment(
    total - principalPaid,
    costings.interestRate,
    costings.termMonths -
      (isMonthlyPaymentAffected ? (costings.promoTermMonths ?? 0) : 0)
  );

  return {
    monthlyPayment,
    promoMonthlyPayment,
  };
}

export const resolveFinancingOption = (
  defaultFinancing: FinancingRateCosting | { id?: string } | undefined,
  financing: {
    defaultFinancingConfig: FinancingRateCosting;
    financingOptions: FinancingRateCosting[];
  }
): FinancingRateCosting =>
  financing.financingOptions.find(
    (option) => option.id === defaultFinancing?.id
  ) ||
  (defaultFinancing && 'interestRate' in defaultFinancing
    ? defaultFinancing
    : financing.defaultFinancingConfig);

export const buildFinancingOption = (
  option: FinancingRateCosting,
  payment: { monthlyPayment: number; promoMonthlyPayment: number }
): FinancingOptionWithPayment => ({
  id: option.id,
  name: option.name,
  interestRate: option.interestRate,
  termMonths: option.termMonths,
  monthlyPayment: payment.monthlyPayment,
  promoMonthlyPayment: payment.promoMonthlyPayment,
  promoEnabled: option.promoEnabled,
  promoInterestRate: option.promoInterestRate ?? 0,
  promoTermMonths: option.promoTermMonths ?? 0,
  promoNoPaymentEnabled: option.promoNoPaymentEnabled ?? false,
  applicationUrl: option.applicationUrl,
});
